import I18n from '@utils/i18n.utils';
import dayjs, {globalWeekDaysShort} from '@utils/dayjs.utils';
import {ServiceKindTypeEnum} from '@store/settings/serviceKinds';
import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';
 
export enum StatisticsMode {
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export namespace StatisticsMode {
  export const subtractUnit = (mode: StatisticsMode) => {
    switch (mode) {
      case StatisticsMode.WEEK:
        return 'w';
      case StatisticsMode.MONTH:
        return 'M';
      case StatisticsMode.YEAR:
        return 'y';
    }
  };

  export const selectItems = () => [
    {label: I18n.t('dashboard.mode.week'), value: StatisticsMode.WEEK},
    {label: I18n.t('dashboard.mode.month'), value: StatisticsMode.MONTH},
    {label: I18n.t('dashboard.mode.year'), value: StatisticsMode.YEAR},
  ];

  export const labels = (filters: StatisticsFilters) => {
    const labels: string[] = [];

    let fromDate;

    switch (filters.mode) {
      case StatisticsMode.WEEK:
        fromDate = dayjs(filters.date).startOf('w');
        Array.from(Array(7)).forEach((_, index) => {
          const date = fromDate.add(index, 'd');
          labels.push(globalWeekDaysShort[date.isoWeekday() - 1]);
        });
        return labels;
      case StatisticsMode.MONTH:
        fromDate = dayjs(filters.date).startOf('month');
        const toDate = dayjs(filters.date).endOf('month');
        Array.from(Array(Math.abs(fromDate.diff(toDate, 'd')) + 1)).forEach((_, index) => {
          const date = fromDate.add(index, 'd');
          labels.push(`${globalWeekDaysShort[date.isoWeekday() - 1]} ${date.date()}`);
        });
        return labels;
      case StatisticsMode.YEAR:
        fromDate = dayjs(filters.date).startOf('year');
        Array.from(Array(12)).forEach((_, index) => {
          const date = fromDate.add(index, 'M');
          labels.push(dayjs.monthsShort()[date.month()]);
        });
        return labels;
    }
  };
}

export namespace StatisticsFilters {
  export const initTableFilters = () => TableFilterType.initTableFilters([
    TableFilterType.CLIENT_ORGANISATION,
    TableFilterType.CLIENT_ORGANISATION_UNIT,
    TableFilterType.WASTE_FAMILY,
    TableFilterType.WASTE,
    TableFilterType.CONTAINER,
  ]);
}

export interface StatisticsFilters {
  mode: StatisticsMode;
  date: Date;
  tableFilters: TableFilter[];
}

export interface TotalTurnoverAndCharges {
  turnover: number;
  charges: number;
}

export interface BarChart {
  executionLinesClientNotIntegrated: number;
  executionLinesClientEligible: number;
  executionLinesSupplierNotIntegrated: number;
  executionLinesSupplierEligible: number;
}

export interface InvoicingProgression {
  supplierLinesEligible: number;
  supplierLinesIntegrated: number;
  clientLinesEligible: number;
  clientLinesIntegrated: number;
}

export interface MarginByLine {
  executionLinesTotal: number;
  executionLinesPositive: number;
  executionLinesNegative: number;
}

export interface TurnoverChargesByServiceKind {
  serviceKindType: string;
  turnover: number;
  charges: number;
}

export interface ExecutionCountByServiceKind {
  serviceKindName: string;
  serviceKindType: ServiceKindTypeEnum;
  execCount: number;
}

export interface ValoWeightByWasteFamily {
  wasteFamilyName: string;
  weight: number;
}

export interface CurveData {
  date: Date;
  value: number;
}

export interface CruveChart {
  clientData: CurveData[];
  supplierData: CurveData[];
}

export interface TurnoverAndPurchase {
  turnoverExecLines: number;
  turnoverInvoiceLines: number;
  purchaseExecLines: number;
  purchaseInvoiceLines: number;
  executedAt: Date;
}

export interface ClientTurnover {
  percentVariation: number;
  variation: number;
  totalAmountNoVatInCents: number;
  id: number;
  name: string;
}

export interface FinanceStatistics {
  executionCountByServiceKind?: ExecutionCountByServiceKind[];
  valoWeightByWasteFamily?: ValoWeightByWasteFamily[];
  turnoverAndChargesByServiceKind?: TurnoverChargesByServiceKind[];
  totalTurnover?: TotalTurnoverAndCharges;
  barChart?: BarChart;
  donutChart?: InvoicingProgression;
  curveChart?: CruveChart;
  marginByLine?: MarginByLine;
  turnoverAndPurchaseEvolution?: TurnoverAndPurchase[];
  clientsTurnover?: ClientTurnover[];
}

export interface AverageQuantity {
  weightAverageAmountInKilograms: number;
}

export interface ExpensesByServiceData {
  quantity: number;
  price: number;
  date: string;
}

export interface ExpensesByService {
  name: string;
  stats: ExpensesByServiceData[];
}

export interface ExecAndIssuesCount {
  execCount: number;
  execExecutedCount: number;
  execExecutedPickupCount: number;
  issueCount: number;
  execWithIssueCount: number;
  execTotalWeight: number;
  date: Date;
}

export interface ExecutionAndIssuesEvolution {
  current: ExecAndIssuesCount[];
  old: ExecAndIssuesCount[];
}

export interface EvolutionOfOperations {
  evolution: ExecutionAndIssuesEvolution;
  execCreatedCount: { current: number; old: number };
  execExecutedCount: { current: number; old: number };
  execExecutedPickupCount: { current: number; old: number };
  execPickupWeight: { current: number; old: number };
  execProcessingWeight: { current: number; old: number };
  execValorisationWeight: { current: number; old: number };
  execTheoPickupWeight: { current: number; old: number };

}

export interface OperationsCount {
  executionCount: number;
  previousExecutionCount: number;
}

export interface OperationsStatistics {
  operationsCount?: OperationsCount;
  evolutionOfOperations?: EvolutionOfOperations;
  expensesByServices?: ExpensesByService[];
  averageQuantity?: AverageQuantity;
}

export interface Statistics {
  filters: StatisticsFilters;
  finance: FinanceStatistics;
  operations: OperationsStatistics;
}
